/**
 * LeftShield Portal - Constitution Engine Page (F035 + W2)
 * Security principles and AI-powered policy impact analysis.
 * Route: /constitution | Category: AI
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Button,
  Spinner,
  Textarea,
  Input,
  tokens,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  SparkleRegular,
  WarningRegular,
  CheckmarkCircleRegular,
  ArrowTrendingRegular,
} from '@fluentui/react-icons';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { constitutionApi, type PolicyImpactAnalysis } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';

// ---------------------------------------------------------------------------
// Starter security principles (organizational defaults)
// ---------------------------------------------------------------------------

interface SecurityPrinciple {
  id: string;
  name: string;
  description: string;
  scope: 'All Frameworks' | string;
  enforcement: 'Mandatory' | 'Advisory';
  status: 'Active';
  downstreamImpact: {
    policiesDerived: number;
    controlsAffected: number;
    violations: number;
    evidenceRecords: number;
  };
}

const STARTER_PRINCIPLES: SecurityPrinciple[] = [
  {
    id: 'p1',
    name: 'Encryption at rest is mandatory',
    description: 'All data stores must use encryption at rest with platform-managed or customer-managed keys.',
    scope: 'All Frameworks',
    enforcement: 'Mandatory',
    status: 'Active',
    downstreamImpact: { policiesDerived: 4, controlsAffected: 8, violations: 2, evidenceRecords: 156 },
  },
  {
    id: 'p2',
    name: 'No public storage buckets',
    description: 'Cloud storage containers (S3, Blob, GCS) must not have public access enabled.',
    scope: 'All Frameworks',
    enforcement: 'Mandatory',
    status: 'Active',
    downstreamImpact: { policiesDerived: 2, controlsAffected: 5, violations: 0, evidenceRecords: 89 },
  },
  {
    id: 'p3',
    name: 'MFA required for admin accounts',
    description: 'All administrative and privileged accounts must enforce multi-factor authentication.',
    scope: 'All Frameworks',
    enforcement: 'Mandatory',
    status: 'Active',
    downstreamImpact: { policiesDerived: 3, controlsAffected: 6, violations: 0, evidenceRecords: 210 },
  },
  {
    id: 'p4',
    name: 'Secrets must not appear in source code',
    description: 'API keys, passwords, and tokens must never be committed to version control.',
    scope: 'All Frameworks',
    enforcement: 'Mandatory',
    status: 'Active',
    downstreamImpact: { policiesDerived: 3, controlsAffected: 4, violations: 1, evidenceRecords: 342 },
  },
  {
    id: 'p5',
    name: 'Dependencies must be scanned for vulnerabilities',
    description: 'All third-party dependencies must be regularly scanned for known CVEs before deployment.',
    scope: 'All Frameworks',
    enforcement: 'Advisory',
    status: 'Active',
    downstreamImpact: { policiesDerived: 2, controlsAffected: 7, violations: 3, evidenceRecords: 128 },
  },
  {
    id: 'p6',
    name: 'Network segmentation for production',
    description: 'Production environments must be isolated from development and staging networks.',
    scope: 'All Frameworks',
    enforcement: 'Mandatory',
    status: 'Active',
    downstreamImpact: { policiesDerived: 2, controlsAffected: 3, violations: 0, evidenceRecords: 67 },
  },
];

const getRiskColor = (level: string): string => {
  switch (level.toUpperCase()) {
    case 'HIGH': return tokens.colorPaletteRedForeground1;
    case 'MEDIUM': return tokens.colorPaletteYellowForeground1;
    case 'LOW': return tokens.colorPaletteGreenForeground1;
    default: return tokens.colorNeutralForeground3;
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ConstitutionEnginePage: React.FC = () => {
  const ps = usePageStyles();
  const navigate = useNavigate();
  const [proposedChange, setProposedChange] = useState('');
  const [environmentHint, setEnvironmentHint] = useState('');
  const [assetHint, setAssetHint] = useState('');
  const [analysisResult, setAnalysisResult] = useState<PolicyImpactAnalysis | null>(null);

  const analyzeMutation = useMutation({
    mutationFn: async (changeDescription: string) => {
      return constitutionApi.analyzeImpact(
        changeDescription,
        environmentHint || undefined,
        assetHint || undefined,
      );
    },
    onSuccess: (result) => {
      setAnalysisResult(result);
    },
  });

  const handleAnalyze = () => {
    if (!proposedChange.trim()) return;
    setAnalysisResult(null);
    analyzeMutation.mutate(proposedChange.trim());
  };

  // Auth error detection
  const queryError = analyzeMutation.error as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isUnauth) {
    return (
      <div className={ps.page}>
        <PageHeader title="Core Enforcement Rules" subtitle="Foundational enforcement rules that constrain all governance decisions. Every rule maps to active violations and enforcement status." showAIButton={false} />
        <UnauthenticatedBanner feature="constitution engine" />
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader title="Core Enforcement Rules" subtitle="Foundational enforcement rules that constrain all governance decisions. Every rule maps to active violations and enforcement status." />

      {/* Security Principles */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>Security Principles</Title2>
            <Badge appearance="outline" color="informative" icon={<ShieldCheckmarkRegular />}>
              {STARTER_PRINCIPLES.length} active
            </Badge>
          </div>

          <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
            Constitutional rules that govern your organization's security posture. These principles are enforced across all compliance frameworks.
          </Body2>

          <div className={ps.rowList}>
            {STARTER_PRINCIPLES.map((p) => (
              <div key={p.id} className={ps.rowItem} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flex: 1 }}>
                    <Body1 style={{ fontWeight: 600 }}>{p.name}</Body1>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{p.description}</Body2>
                  </div>
                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexShrink: 0 }}>
                    <Badge appearance="outline" size="small">{p.scope}</Badge>
                    <Badge
                      appearance="tint"
                      color={p.enforcement === 'Mandatory' ? 'danger' : 'warning'}
                      size="small"
                    >
                      {p.enforcement}
                    </Badge>
                    <Badge appearance="tint" color="success" size="small">{p.status}</Badge>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalL, marginTop: tokens.spacingVerticalXS }}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                    {p.downstreamImpact.policiesDerived} policies
                  </Body2>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                    {p.downstreamImpact.controlsAffected} controls
                  </Body2>
                  <Body2 style={{ color: p.downstreamImpact.violations > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground3 }}>
                    {p.downstreamImpact.violations} violations
                  </Body2>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                    {p.downstreamImpact.evidenceRecords} evidence
                  </Body2>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Policy Impact Analyzer — F045: same capability as Simulation -> Execution Simulator,
          cross-linked rather than redirected (this page is Core Enforcement Rules / policy
          authoring, much broader than simulation, and this is one widget within it rather
          than a duplicate SURFACE — see iaSurfaceRegistry.ts /twin entry note + PROGRESS.md
          "What F045 deliberately did not do" gap #2). */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Title2>Policy Impact Analyzer</Title2>
              <Badge appearance="outline" color="brand" icon={<SparkleRegular />}>
                AI-Powered
              </Badge>
            </div>
            <Button appearance="subtle" size="small" onClick={() => navigate('/execution-simulator')}>
              View Full Simulation
            </Button>
          </div>

          <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
            Describe a proposed policy change and get an AI-powered risk assessment of its impact on your security posture.
          </Body2>

          <Textarea
            placeholder="Example: We want to allow public read access to our marketing assets S3 bucket to serve static website content..."
            value={proposedChange}
            onChange={(_, data) => setProposedChange(data.value)}
            resize="vertical"
            style={{ minHeight: '120px' }}
          />

          <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Environment (optional)</Caption1>
              <Input
                placeholder="e.g., production"
                value={environmentHint}
                onChange={(_, data) => setEnvironmentHint(data.value)}
                size="small"
                style={{ width: '160px' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Asset (optional)</Caption1>
              <Input
                placeholder="e.g., customer-data-bucket"
                value={assetHint}
                onChange={(_, data) => setAssetHint(data.value)}
                size="small"
                style={{ width: '200px' }}
              />
            </div>
            <Button
              appearance="primary"
              icon={<SparkleRegular />}
              onClick={handleAnalyze}
              disabled={!proposedChange.trim() || analyzeMutation.isPending}
            >
              Analyze Impact
            </Button>
            {analyzeMutation.isPending && <Spinner size="tiny" label="Analyzing..." />}
          </div>

          {analyzeMutation.isError && (
            <div className={ps.statTile} style={{ backgroundColor: tokens.colorPaletteRedBackground1 }}>
              <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>
                {analyzeMutation.error instanceof Error
                  ? analyzeMutation.error.message
                  : 'Unable to analyze policy change. Ensure the AI Narrator is configured.'}
              </Body1>
            </div>
          )}

          {/* Structured Analysis Result */}
          {analysisResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, marginTop: tokens.spacingVerticalM }}>
              {/* Risk Summary Bar */}
              <div style={{
                display: 'flex',
                gap: tokens.spacingHorizontalL,
                alignItems: 'center',
                padding: tokens.spacingVerticalM,
                borderRadius: tokens.borderRadiusMedium,
                backgroundColor: tokens.colorNeutralBackground2,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                  <WarningRegular style={{ fontSize: '24px', color: getRiskColor(analysisResult.riskLevel) }} />
                  <div>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Risk Level</Caption1>
                    <Body1 style={{ fontWeight: 600, color: getRiskColor(analysisResult.riskLevel) }}>{analysisResult.riskLevel}</Body1>
                  </div>
                </div>
                <div>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Confidence</Caption1>
                  <Body1 style={{ fontWeight: 600 }}>{Math.round(analysisResult.confidenceScore * 100)}%</Body1>
                </div>
                <div>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Score Delta</Caption1>
                  <Body1 style={{ fontWeight: 600, color: analysisResult.estimatedImpact.governanceScoreDelta < 0 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}>
                    {analysisResult.estimatedImpact.governanceScoreDelta > 0 ? '+' : ''}{analysisResult.estimatedImpact.governanceScoreDelta}
                  </Body1>
                </div>
                <div>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Exposure</Caption1>
                  <Body1 style={{ fontWeight: 600, color: analysisResult.estimatedImpact.exposureDelta > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground1 }}>
                    +{analysisResult.estimatedImpact.exposureDelta}%
                  </Body1>
                </div>
              </div>

              {/* Executive Summary */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                <Body1 style={{ fontWeight: 600 }}>Executive Summary</Body1>
                <Body2 style={{ whiteSpace: 'pre-wrap' }}>{analysisResult.recommendation.executiveSummary}</Body2>
              </div>

              {/* Triggered Policies */}
              {analysisResult.triggeredPolicies.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
                  <Body1 style={{ fontWeight: 600 }}>Triggered Policies</Body1>
                  {analysisResult.triggeredPolicies.map((policy, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                      <Badge
                        appearance="tint"
                        color={policy.expectedOutcome === 'BLOCK' ? 'danger' : policy.expectedOutcome === 'WARN' ? 'warning' : 'success'}
                        size="small"
                      >
                        {policy.expectedOutcome}
                      </Badge>
                      <Body2>{policy.policyName}</Body2>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        ({Math.round(policy.triggerLikelihood * 100)}% likelihood)
                      </Body2>
                    </div>
                  ))}
                </div>
              )}

              {/* Affected Principles */}
              {analysisResult.affectedPrinciples.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
                  <Body1 style={{ fontWeight: 600 }}>Affected Principles</Body1>
                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' }}>
                    {analysisResult.affectedPrinciples.map((p, i) => (
                      <Badge key={i} appearance="outline" color="danger" icon={<ShieldCheckmarkRegular />}>{p}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Affected Controls */}
              {analysisResult.affectedControls.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
                  <Body1 style={{ fontWeight: 600 }}>Affected Controls</Body1>
                  {analysisResult.affectedControls.map((ctrl, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                      <Badge appearance="outline" size="small">{ctrl.frameworkCode}</Badge>
                      <Body2 style={{ fontWeight: 500 }}>{ctrl.controlId}</Body2>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{ctrl.controlName}</Body2>
                      <Badge
                        appearance="tint"
                        color={ctrl.impactType === 'violated' ? 'danger' : 'warning'}
                        size="small"
                      >
                        {ctrl.impactType}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Technical Explanation */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                <Body1 style={{ fontWeight: 600 }}>Technical Details</Body1>
                <Body2 style={{ whiteSpace: 'pre-wrap', color: tokens.colorNeutralForeground2 }}>
                  {analysisResult.recommendation.technicalExplanation}
                </Body2>
              </div>

              {/* Safer Alternative */}
              {analysisResult.recommendation.saferAlternative && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.spacingVerticalXS,
                  padding: tokens.spacingVerticalM,
                  borderRadius: tokens.borderRadiusMedium,
                  backgroundColor: tokens.colorPaletteGreenBackground1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                    <Body1 style={{ fontWeight: 600, color: tokens.colorPaletteGreenForeground1 }}>Safer Alternative</Body1>
                  </div>
                  <Body2>{analysisResult.recommendation.saferAlternative}</Body2>
                </div>
              )}

              {/* Exception Path */}
              {analysisResult.recommendation.exceptionPath && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.spacingVerticalXS,
                  padding: tokens.spacingVerticalM,
                  borderRadius: tokens.borderRadiusMedium,
                  backgroundColor: tokens.colorNeutralBackground2,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <ArrowTrendingRegular />
                    <Body1 style={{ fontWeight: 600 }}>Exception Path</Body1>
                  </div>
                  <Body2>{analysisResult.recommendation.exceptionPath}</Body2>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
